import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { AppRoutes } from "@/routes";
import { StartTimerDialog } from "@/components/timer/StartTimerDialog";
import { ActiveTimerBar } from "@/components/timer/ActiveTimerBar";
import { getDataAdapter } from "@/data";
import { useAuth } from "@/auth/AuthProvider";
import { useTimer } from "@/hooks/useTimer";
export function App() {
    const { user } = useAuth();
    const adapter = getDataAdapter();
    const timer = useTimer();
    const [showStart, setShowStart] = useState(false);
    const [approvalCount, setApprovalCount] = useState(0);
    const [hasDirectReports, setHasDirectReports] = useState(false);
    const [staffScopeError, setStaffScopeError] = useState(null);
    const [staffScopeRetry, setStaffScopeRetry] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [stoppingTimer, setStoppingTimer] = useState(false);
    const [timerError, setTimerError] = useState(null);
    // Wrap timer.stop to expose a loading flag to ActiveTimerBar. Dataverse
    // create can lag for a couple of seconds; without this the user can click
    // Stop repeatedly and queue duplicate creates.
    const handleStop = async () => {
        if (stoppingTimer)
            return;
        setStoppingTimer(true);
        setTimerError(null);
        try {
            await timer.stop();
        }
        catch (err) {
            console.error("Stop timer failed", err);
            setTimerError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setStoppingTimer(false);
        }
    };
    useEffect(() => {
        let alive = true;
        adapter.listPendingForManager(user.id).then((list) => {
            if (alive)
                setApprovalCount(list.length);
        });
        return () => {
            alive = false;
        };
    }, [adapter, user.id, timer.tick]);
    const handleStart = (t) => {
        timer.start(t);
        setShowStart(false);
    };
    useEffect(() => {
        let alive = true;
        setHasDirectReports(false);
        setStaffScopeError(null);
        void adapter.listDirectReports(user.id).then((reports) => {
            if (alive)
                setHasDirectReports(reports.length > 0);
        }).catch((err) => {
            if (!alive)
                return;
            console.error("Could not check direct reports", err);
            setStaffScopeError(err instanceof Error ? err.message : String(err));
        });
        return () => { alive = false; };
    }, [adapter, user.id, staffScopeRetry]);
    return (_jsxs("div", { className: "flex min-h-screen bg-canvas text-slate-900", children: [_jsx(Sidebar, { approvalCount: approvalCount, hasDirectReports: hasDirectReports, mobileOpen: drawerOpen, onMobileClose: () => setDrawerOpen(false) }), _jsxs("div", { className: "flex flex-1 flex-col min-w-0", children: [_jsx(TopBar, { onStartTimer: () => setShowStart(true), onMenuClick: () => setDrawerOpen(true) }), timer.active && (_jsx(ActiveTimerBar, { timer: timer.active, onStop: handleStop, stopping: stoppingTimer })), timerError && (_jsxs("div", { role: "alert", className: "bg-rose-50 px-6 py-3 text-sm text-rose-700", children: ["Could not save the timer entry: ", timerError, ". The timer is still running; retry Stop."] })), staffScopeError && _jsxs("div", { role: "alert", className: "bg-rose-50 px-6 py-3 text-sm text-rose-700", children: ["Could not check Staff Entries access: ", staffScopeError, ".", " ", _jsx("button", { className: "underline font-medium", onClick: () => setStaffScopeRetry((value) => value + 1), children: "Retry" })] }), _jsx("main", { className: "flex-1 overflow-y-auto", children: _jsx(AppRoutes, {}) })] }), showStart && (_jsx(StartTimerDialog, { onClose: () => setShowStart(false), onStart: handleStart }))] }));
}
